import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LabOrder } from "../../interfaces/LabOrder";
import { getLabOrderById } from "../../apis/LabOrder";
import { useNotification } from "../../NotificationContext";

const ViewLabOrder: React.FC = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const orderId = id ? parseInt(id) : 0;
  const [order, setOrder] = useState<LabOrder | null>(null);
  const [isFetching, setIsFetching] = useState(true);
  const [testResults, setTestResults] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails();
    }
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      setIsFetching(true);
      const res = await getLabOrderById(orderId);
      const orderData = res.data.data;
      setOrder(orderData);
      
      // Initialize test results from existing data
      // API structure: sections are at root level, not in items
      const results: { [key: string]: string } = {};
      
      // Check if sections are at root level (new API structure)
      if (orderData?.sections && orderData.sections.length > 0) {
        orderData.sections.forEach((section: any) => {
          if (section.parameters && section.parameters.length > 0) {
            section.parameters.forEach((param: any) => {
              // Use order ID and parameter ID for the key
              const key = `${orderData.id}-${param.id}`;
              // Result can be an object with value property or a string
              results[key] = param.result?.value || param.result || "";
            });
          }
        });
      }
      // Fallback: Check if items exist (old structure)
      else if (orderData?.items) {
        orderData.items.forEach((item: any) => {
          if (item.sections && item.sections.length > 0) {
            item.sections.forEach((section: any) => {
              if (section.parameters && section.parameters.length > 0) {
                section.parameters.forEach((param: any) => {
                  const key = `${item.id}-${param.id}`;
                  results[key] = param.result?.value || param.result || "";
                });
              }
            });
          }
          else if (item.parameters && item.parameters.length > 0) {
            item.parameters.forEach((param: any) => {
              const key = `${item.id}-${param.id}`;
              results[key] = param.result || param.value || "";
            });
          }
        });
      }
      
      setTestResults(results);
    } catch (err) {
      console.error("Error fetching order details:", err);
      showNotification("error", "Failed to load order details", "", "topRight");
    } finally {
      setIsFetching(false);
    }
  };

  if (isFetching) {
    return (
      <div className="max-w-6xl p-6">
        <div className="text-center">Loading order details...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-6xl p-6">
        <div className="text-center text-red-600">Order not found</div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-gray-900">
          Lab Order Details
        </h1>
        <p className="mt-1 text-sm sm:text-base font-medium text-gray-900">
          Order Number - {order.orderNumber}
        </p>
        <p className="mt-1 text-sm sm:text-base font-medium text-gray-900 capitalize">
          Patient Name - {order.patient
            ? `${order.patient.firstName || ""} ${order.patient.lastName || ""}`.trim()
            : `Patient #${order.patientId}`}
        </p>
        <p className="mt-1 text-sm sm:text-base font-medium text-gray-900">
          Sample Id - {order.sampleId}
        </p>

        <div className="flex items-center justify-end gap-3 border-gray-200">
        <button
          type="button"
          className="px-6 py-2 text-sm font-semibold text-white bg-primary border border-gray-300 rounded-md"
        >
          Print
        </button>
      </div>
      </div>


      {/* Tests Section */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        {(() => {
          // Handle new API structure: sections at root level, single lab_test
          const orderData = order as any;
          const sections = orderData?.sections || [];
          const labTest = orderData?.lab_test || orderData?.labTest;
          const testName = labTest?.name || "Unknown Test";
          
          // If sections exist at root level (new structure)
          if (sections.length > 0) {
            return (
              <div>
                {/* Test Header */}
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h3 className="text-base font-semibold text-gray-900 uppercase text-center">
                    {testName}
                  </h3>
                </div>

                {/* Sections and Parameters - Single Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-300">
                          Parameter
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-300">
                          Result
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-300">
                          Unit
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Reference Range
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sections.map((section: any, sectionIndex: number) => {
                        const sectionParams = section.parameters || [];
                        if (sectionParams.length === 0) return null;

                        return (
                          <React.Fragment key={section.id || sectionIndex}>
                            {/* Section Header Row */}
                            <tr className="bg-gray-100">
                              <td 
                                colSpan={4}
                                className="px-4 py-2 text-sm font-semibold text-gray-900 uppercase border-b border-gray-300"
                              >
                                {section.name}
                              </td>
                            </tr>
                            {/* Parameters Rows */}
                            {sectionParams.map((param: any, paramIndex: number) => {
                              const key = `${orderData.id}-${param.id}`;
                              // Get result value - can be from state, param.result.value, or param.result
                              const resultValue = testResults[key] || param.result?.value || param.result || "";
                              
                              // Calculate reference range correctly
                              let referenceRange = "Absent";
                              const refText = param.reference_text || param.referenceText;
                              const refMin = param.reference_min || param.referenceMin;
                              const refMax = param.reference_max || param.referenceMax;
                              
                              if (refText) {
                                referenceRange = refText;
                              } else if (
                                refMin !== null &&
                                refMin !== undefined &&
                                refMin !== "" &&
                                refMax !== null &&
                                refMax !== undefined &&
                                refMax !== ""
                              ) {
                                referenceRange = `${refMin} - ${refMax}`;
                              } else if (
                                refMin !== null &&
                                refMin !== undefined &&
                                refMin !== ""
                              ) {
                                referenceRange = `≥ ${refMin}`;
                              } else if (
                                refMax !== null &&
                                refMax !== undefined &&
                                refMax !== ""
                              ) {
                                referenceRange = `≤ ${refMax}`;
                              }

                              return (
                                <tr key={param.id || `${section.id}-${paramIndex}`} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-300 font-medium">
                                    {param.name || "-"}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-300">
                                    {resultValue || "-"}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-300">
                                    {param.unit || "-"}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-700">
                                    {referenceRange}
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          }
          
          // Fallback: Handle old structure with items array
          if (order.items && order.items.length > 0) {
            return order.items.map((item: any, itemIndex: number) => {
              const itemTestName = item.lab_test?.name || item.labTest?.name || item.name || "Unknown Test";
              const itemSections = item.sections || [];

              return (
                <div key={itemIndex} className={itemIndex > 0 ? "border-t border-gray-200" : ""}>
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <h3 className="text-base font-semibold text-gray-900 uppercase">
                      {itemTestName}
                    </h3>
                  </div>
                  {itemSections.length > 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      Sections found in items (old structure)
                    </div>
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      No parameters found for this test
                    </div>
                  )}
                </div>
              );
            });
          }
          
          // No tests found
          return (
            <div className="p-8 text-center text-gray-500">
              No tests found in this order
            </div>
          );
        })()}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-6 mt-6 border-t border-gray-200">
        <button
          onClick={() => navigate("/lab-order-list")}
          type="button"
          className="px-6 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Back
        </button>
      </div>
    </div>
  );
};

export default ViewLabOrder;

